import React, { useEffect, useMemo, useState } from "react";
import useDashboard from "../../hooks/useDashboard";
import InputBox from "../../components/InputBox";
import PieArcLabel from "./PieChart";
import CommonTable from "../../components/CommonTable";
import CommonBarChart from "./CommonBarChart";
import { PieData } from "../../../../shared/services/interface";
import { Table, Toporders } from "../setting/settingInterface";
import GenerateDailyReport from "src/business-types/laundry/modules/dashboard/GenerateReport";

// 🔹 Constants
const COLORS = ["#3b82f6", "#2980b9", "#eab308", "#ef4444", "#10b981", "#8b5cf6"];

const ORDER_TABLE: Table<Toporders>[] = [
  { id: "S.No", label: "S.No", numeric: false },
  { id: "customerName", label: "Customer Name", numeric: false },
  { id: "orderPrice", label: "Total Purchases", numeric: false },
];

// 🔹 Utility functions
const getColorByIndex = (index: number) => COLORS[index % COLORS.length];

const toPieData = (source: any[], valueKey: string, labelKey: string): PieData[] =>
  source?.map((item, index) => ({
    id: index,
    value: item[valueKey],
    label: item[labelKey],
    color: getColorByIndex(index),
  })) || [];

const toTopOrders = (source: any[], labelKey: string, valueKey: string): Toporders[] =>
  source?.map((item, index) => ({
    id: String(index),
    "S.No": index + 1,
    customerName: item[labelKey],
    orderPrice: String(item[valueKey]),
  })) || [];

// 🔹 Summary Card Component
const SummaryCard = ({ title, value, emoji }: { title: string; value?: number | string; emoji?: string }) => (
  <div className='p-4 bg-white shadow border rounded-lg flex flex-col items-center justify-center text-center'>
    <div className='flex items-center gap-2 text-gray-700'>
      {emoji && <span className='text-xl'>{emoji}</span>}
      <p className='text-sm font-semibold'>{title}</p>
    </div>
    <p className='text-2xl font-bold mt-1'>{value ?? 0}</p>
  </div>
);

// 🔹 Pie + Table Section
const PieTableSection = ({ title, pieData, tableData }: { title: string; pieData: PieData[]; tableData: Toporders[] }) => (
  <div className='bg-white p-4 shadow rounded-lg flex flex-col items-center gap-4'>
    <PieArcLabel piechartData={pieData} Header={title} />
    <CommonTable<Toporders> data={tableData} headCells={ORDER_TABLE} title={title} selectable={false} />
  </div>
);

function Dashboard() {
  const now = new Date();
  const today = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0") + "-" + String(now.getDate()).padStart(2, "0");
  const [date, setDate] = useState({ From: today, To: today });
  const { dashboard } = useDashboard(date.From, date.To);
  const [barChart, setBarChart] = useState<any[]>([]);
  const [reportGenerated, setReportGenerated] = useState(false);

  // 🔹 Update bar chart when dashboard data changes
  useEffect(() => {
    const sixMonths = dashboard?.monthly_sales_inward_summary?.last_six_months;
    if (sixMonths) setBarChart(sixMonths);
  }, [dashboard]);

  // 🔹 Handle date change
  const handleDateChange = (key: "From" | "To", newDate: string) => {
    setDate((prev) => {
      if (key === "From" && newDate > prev.To) return { From: newDate, To: newDate };
      if (key === "To" && newDate < prev.From) return { From: newDate, To: newDate };
      return { ...prev, [key]: newDate };
    });
  };

  // 🔹 Derived Data (Memoized)
  const barChartData = useMemo(
    () =>
      barChart.map((item) => ({
        name: item.month,
        sales: item.total_sales,
        purchases: item.total_stock_inward_price,
      })),
    [barChart],
  );

  const pieOrder = useMemo(() => toPieData(dashboard?.top_orders || [], "total_amount", "customer_name"), [dashboard]);

  const pieCustomer = useMemo(() => toPieData(dashboard?.top_customers || [], "totalSpent", "CustomerName"), [dashboard]);

  const customerData = useMemo(() => toTopOrders(dashboard?.top_customers || [], "CustomerName", "totalSpent"), [dashboard]);

  const orderData = useMemo(() => toTopOrders(dashboard?.top_orders || [], "customer_name", "total_amount"), [dashboard]);

  const lastSixMonths = dashboard?.monthly_sales_inward_summary?.last_six_months || [];
  const lastTwelveMonths = dashboard?.monthly_sales_inward_summary?.last_twelve_months || [];

  return (
    <div className='w-full h-full flex flex-col gap-6 p-4 overflow-auto'>
      {/* 🔹 Date Filters */}
      <div className='flex flex-wrap gap-4'>
        {Object.entries(date).map(([key, value]) => (
          <div className='flex flex-col' key={key}>
            <label className='text-sm font-semibold'>{key}</label>
            <InputBox
              type='date'
              value={value}
              max={key === "To" ? today : date.To}
              min={key === "To" ? date.From : undefined}
              onChange={(e) => handleDateChange(key as "From" | "To", e.target.value)}
            />
          </div>
        ))}
        {reportGenerated && (
          <div className='mt-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3'>
            The report has been sent to your email. For date-wise reports, please visit the Report tab to view or download them.{" "}
          </div>
        )}
        {/* <div className="mt-4">
          <GenerateDailyReport onReportGenerated={() => setReportGenerated(true)} />
        </div> */}
      </div>

      {/* 🔹 Summary Cards */}
      <div className='grid sm:grid-cols-3 lg:grid-cols-5 gap-4'>
        <SummaryCard title='Sales' value={dashboard?.total_sales.toFixed(2)} emoji='💰' />
        <SummaryCard title='Current Month Sales' value={lastSixMonths[5]?.total_sales.toFixed(2)} emoji='📈' />
        <SummaryCard title='Purchases' value={dashboard?.stock_summary?.total_buyprice_sum.toFixed(2)} emoji='🛒' />
        <SummaryCard title='Current Month Purchases' value={lastSixMonths[5]?.total_stock_inward_price.toFixed(2)} emoji='📦' />
        <SummaryCard title='Total Outstanding' value={dashboard?.overall_outstanding_balance.toFixed(2)} emoji='💳' />
      </div>

      {/* 🔹 Bar Chart Section */}
      <div className='flex flex-col items-center'>
        <div className='flex gap-4 mb-2'>
          <label className='flex items-center gap-2 cursor-pointer'>
            <input type='radio' name='barChart' className='w-5 h-5' onClick={() => setBarChart(lastSixMonths)} defaultChecked />
            Semi Annually
          </label>
          <label className='flex items-center gap-2 cursor-pointer'>
            <input type='radio' name='barChart' className='w-5 h-5' onClick={() => setBarChart(lastTwelveMonths)} />
            Annually
          </label>
        </div>
        <CommonBarChart data={barChartData} Header='Sales vs Purchases' />
      </div>

      {/* 🔹 Pie + Table Section */}
      <div className='grid lg:grid-cols-2 gap-6'>
        <PieTableSection title='Top Orders' pieData={pieOrder} tableData={orderData} />
        <PieTableSection title='Top Customers' pieData={pieCustomer} tableData={customerData} />
      </div>
    </div>
  );
}

export default Dashboard;
